import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import Alert, { AlertProps } from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useDialogs } from '@toolpad/core/useDialogs';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';

import { useResetPassword } from 'api/hooks/public/auth/useResetPassword';

import FeedbackDialog from 'components/Dialogs/FeedbackDialog';
import TextInput from 'components/FormInputs/TextInput';
import PasswordRequirements from 'components/PasswordRequirements';

import { useExternalMode } from 'hooks/useExternalMode';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';

const formSchema = z
  .object({
    password: z
      .string({ required_error: 'Password is required' })
      .regex(
        /^(?=.*[0-9])(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/,
        'Password does not meet the minimum requirements',
      ),
    confirm_password: z.string({ required_error: 'Confirm your password' }),
  })
  .refine((data) => data.password === data.confirm_password, {
    path: ['confirm_password'],
    message: 'Passwords do not match',
  });

type FormValues = z.infer<typeof formSchema>;

function Form() {
  const [searchParams] = useSearchParams();
  const dialogs = useDialogs();
  const navigate = useNavigate();
  const [alertData, setAlertData] = useState<{ message: string; severity: AlertProps['severity'] } | null>(null);
  const { isExternalMode } = useExternalMode();

  const resetPassword = useResetPassword({
    onSuccess: async () => {
      // show success message
      await dialogs
        .open(FeedbackDialog, {
          icon: 'success',
          title: 'Success',
          message: 'Your password has been updated.',
          okText: 'Login To Your Account',
        })
        .then(() => {
          // navigate to login page
          navigate(isExternalMode ? '/external/login' : '/login');
        });
    },
    onError: (error) => {
      // update the alert data with the error message
      setAlertData({
        message: getApiErrorMessage(error),
        severity: 'error',
      });
    },
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      confirm_password: '',
    },
  });

  const { handleSubmit } = form;

  const onSubmit = async ({ password }: FormValues) => {
    const resetToken = searchParams.get('token');

    if (!resetToken) {
      setAlertData({
        message: 'Invalid reset link',
        severity: 'error',
      });
      return;
    }

    resetPassword.mutate({
      token: resetToken,
      newPassword: password,
    });
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={2}>
          <Box>
            <Typography
              sx={{
                textTransform: 'uppercase',
                fontWeight: 'medium',
              }}
              gutterBottom
              color={'text.secondary'}
            >
              Recover account
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
              }}
            >
              Forgot your password?
            </Typography>
            <Typography color="text.secondary">
              Enter your email address below and we&apos;ll get you back on track.
            </Typography>
          </Box>

          <Collapse in={!!alertData && !resetPassword.isPending}>
            <Alert variant="outlined" severity={alertData?.severity}>
              {alertData?.message}
            </Alert>
          </Collapse>

          <TextInput name="password" label="Password" type="password" />
          <TextInput name="confirm_password" label="Confirm Password" type="password" />

          <PasswordRequirements
            fieldName="password"
            requirements={[
              {
                title: 'Contains at least one digit (0-9)',
                regex: /(?=.*[0-9])/,
              },
              {
                title: 'Contains at least one special character from the set (![!@#$%^&*])',
                regex: /(?=.*[!@#$%^&*])/,
              },
              {
                title: 'Contains at least one lowercase letter (a-z)',
                regex: /(?=.*[a-z])/,
              },
              {
                title: 'Contains at least one uppercase letter (A-Z)',
                regex: /(?=.*[A-Z])/,
              },
              {
                title: 'Is at least 8 characters long',
                regex: /.{8,}/,
              },
            ]}
          />
          <Collapse in={resetPassword.isPending}>
            <LinearProgress />
          </Collapse>

          <Button size="large" variant="contained" type="submit" disabled={resetPassword.isPending}>
            Reset Password
          </Button>
        </Stack>
      </form>
    </FormProvider>
  );
}

export default Form;
